import { useDeferredValue, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'

import { Badge } from '@/components/data-display/badge'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { Input } from '@/components/forms/input'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { IconButton } from '@/components/primitives/icon-button'
import { cn } from '@/lib/cn'

import { agentToolIdentityKey, type AgentToolOption } from './api'
import { ToolSchemaDetails } from './tool-schema-details'
import { ToolSourceMark } from './tool-source-mark'

interface ToolBindingDrawerProps {
  confirmedReviews: Set<string>
  hasDraftChanges: boolean
  isSaving: boolean
  onClose: () => void
  onConfirmReview: (identity: string) => void
  onSave: () => void
  onSelectedChange: (selected: Set<string>) => void
  placement?: 'default' | 'left-secondary'
  saveLabel?: string
  selected: Set<string>
  tools: AgentToolOption[]
}

interface ToolGroup {
  key: string
  label: string
  source: AgentToolOption['source']
  tools: AgentToolOption[]
}

function SearchIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="m10.25 10.25 3 3" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m4.25 4.25 7.5 7.5m0-7.5-7.5 7.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function CollapseIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m6 3.5 4.5 4.5L6 12.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden
      className={cn('size-4 shrink-0 transition-transform', expanded && 'rotate-180')}
      fill="none"
      viewBox="0 0 16 16"
    >
      <path
        d="m4.5 6 3.5 3.5L11.5 6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function toolGroupKey(tool: AgentToolOption) {
  return tool.source === 'builtin' ? 'builtin' : `mcp:${tool.connection?.id ?? 'unknown'}`
}

function unavailableLabel(tool: AgentToolOption, t: ReturnType<typeof useTranslation>['t']) {
  if (tool.unavailable_reason === 'connection_disabled') {
    return t('bindings.status.connectionDisabled')
  }
  if (tool.unavailable_reason === 'tool_missing') {
    return t('bindings.status.toolMissing')
  }
  return t('bindings.status.unavailable')
}

function ToolBindingDetailsPanel({
  placement,
  onClose,
  tool,
}: {
  placement: NonNullable<ToolBindingDrawerProps['placement']>
  onClose: () => void
  tool: AgentToolOption
}) {
  const { t } = useTranslation('adminAgentTools')
  const sourceLabel =
    tool.source === 'builtin'
      ? t('bindings.picker.builtin')
      : (tool.connection?.display_name ?? t('bindings.picker.connectionUnknown'))

  return createPortal(
    <aside
      aria-label={tool.display_name}
      className={cn(
        'fixed inset-x-4 top-4 bottom-4 z-[100] flex flex-col overflow-hidden rounded-xl border border-[var(--color-panel-border)] bg-white shadow-[0_18px_54px_rgba(17,24,39,0.18)]',
        placement === 'left-secondary'
          ? 'lg:top-0 lg:right-[640px] lg:bottom-0 lg:left-auto lg:w-[min(560px,calc(100vw-640px))] lg:rounded-none lg:border-y-0 lg:border-r-0'
          : 'lg:right-[656px] lg:left-auto lg:w-[360px]',
      )}
      role="dialog"
    >
      <header className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b border-[var(--color-divider)] px-4">
        <h3 className="min-w-0 truncate text-sm font-semibold text-[var(--color-text)]">
          {tool.display_name}
        </h3>
        <IconButton
          aria-label={t('connections.tools.closeDetails')}
          className="shrink-0 text-[var(--color-text-muted)]"
          onClick={onClose}
          size="sm"
        >
          <CloseIcon />
        </IconButton>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <ToolSourceMark source={tool.source} />
          <span>{sourceLabel}</span>
          {tool.remote_name && tool.remote_name !== tool.display_name ? (
            <code className="break-all rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5">
              {tool.remote_name}
            </code>
          ) : null}
          {tool.annotations.readOnlyHint === true ? (
            <Badge className="h-5 text-[10px]" variant="neutral">
              {t('bindings.status.remoteReadOnlyHint')}
            </Badge>
          ) : null}
        </div>

        <p className="mt-3 whitespace-pre-wrap text-sm leading-5 text-[var(--color-text-muted)]">
          {tool.description || tool.display_name}
        </p>

        <ToolSchemaDetails inputSchema={tool.input_schema} outputSchema={tool.output_schema} />
      </div>
    </aside>,
    document.body,
  )
}

export function ToolOptionRow({
  checkboxId,
  confirmedReviews,
  disabled = false,
  onConfirmReview,
  onSelectedChange,
  placement = 'default',
  selected,
  tool,
}: {
  checkboxId: string
  confirmedReviews: Set<string>
  disabled?: boolean
  onConfirmReview: (identity: string) => void
  onSelectedChange: (selected: Set<string>) => void
  placement?: NonNullable<ToolBindingDrawerProps['placement']>
  selected: Set<string>
  tool: AgentToolOption
}) {
  const { t } = useTranslation('adminAgentTools')
  const identity = agentToolIdentityKey(tool)
  const isSelected = selected.has(identity)
  const canSelect = tool.source === 'builtin' || Boolean(tool.definition_id)
  const canReviewCurrent = canSelect && tool.unavailable_reason !== 'tool_missing'
  const reviewConfirmed = confirmedReviews.has(identity)
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <>
      <article
        className={cn(
          'grid gap-2 px-4 py-3 transition-colors',
          tool.review_required
            ? 'bg-[#fffaf0]'
            : isSelected
              ? 'bg-[rgba(25,91,255,0.035)]'
              : 'hover:bg-[var(--color-surface-muted)]',
        )}
      >
        <div className="flex items-start gap-3">
          <input
            aria-label={t('bindings.selectTool', { name: tool.display_name })}
            checked={isSelected}
            className="mt-1 size-4 shrink-0 accent-[var(--color-primary)]"
            disabled={disabled || (!isSelected && !canSelect)}
            id={checkboxId}
            onChange={() => {
              if (disabled) {
                return
              }
              const next = new Set(selected)
              if (next.has(identity)) {
                next.delete(identity)
              } else if (canSelect) {
                next.add(identity)
              }
              onSelectedChange(next)
            }}
            type="checkbox"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <label
                className="cursor-pointer break-words text-sm font-medium text-[var(--color-text)]"
                htmlFor={checkboxId}
              >
                {tool.display_name}
              </label>
              {tool.review_required ? (
                <Badge variant="warning">{t('bindings.status.reviewRequired')}</Badge>
              ) : null}
              {!tool.available ? <Badge variant="danger">{unavailableLabel(tool, t)}</Badge> : null}
              {!tool.execution_available ? (
                <Badge variant="neutral">{t('bindings.status.notReady')}</Badge>
              ) : null}
              {tool.annotations.readOnlyHint === true ? (
                <Badge variant="neutral">{t('bindings.status.remoteReadOnlyHint')}</Badge>
              ) : null}
              <button
                aria-label={t('connections.tools.openDetails', { name: tool.display_name })}
                className="ml-auto cursor-pointer text-xs font-medium text-[var(--color-primary)] hover:underline"
                onClick={() => setDetailsOpen(true)}
                type="button"
              >
                {t('bindings.actions.viewDetails')}
              </button>
            </div>
            {tool.description ? (
              <p className="mt-1 line-clamp-2 text-sm leading-5 text-[var(--color-text-muted)]">
                {tool.description}
              </p>
            ) : tool.remote_name ? (
              <p className="mt-1 truncate text-xs text-[var(--color-text-muted)]">
                {tool.remote_name}
              </p>
            ) : null}
          </div>
        </div>

        {tool.review_required && isSelected ? (
          <InlineNotice tone={reviewConfirmed ? 'success' : 'warning'}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                {canReviewCurrent
                  ? t(reviewConfirmed ? 'bindings.review.confirmed' : 'bindings.review.required')
                  : t('bindings.review.missing')}
              </span>
              {canReviewCurrent && !reviewConfirmed && !disabled ? (
                <Button onClick={() => onConfirmReview(identity)} size="sm" variant="secondary">
                  {t('bindings.actions.confirmCurrent')}
                </Button>
              ) : null}
            </div>
          </InlineNotice>
        ) : null}
      </article>
      {detailsOpen ? (
        <ToolBindingDetailsPanel
          onClose={() => setDetailsOpen(false)}
          placement={placement}
          tool={tool}
        />
      ) : null}
    </>
  )
}

export function ToolBindingDrawer({
  confirmedReviews,
  hasDraftChanges,
  isSaving,
  onClose,
  onConfirmReview,
  onSave,
  onSelectedChange,
  placement = 'default',
  saveLabel,
  selected,
  tools,
}: ToolBindingDrawerProps) {
  const { t } = useTranslation('adminAgentTools')
  const checkboxIdPrefix = useId()
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())
  const isLeftSecondary = placement === 'left-secondary'

  const groups = useMemo<ToolGroup[]>(() => {
    const grouped = new Map<string, ToolGroup>()
    for (const tool of tools) {
      const key = toolGroupKey(tool)
      const group = grouped.get(key) ?? {
        key,
        label:
          tool.source === 'builtin'
            ? t('bindings.picker.builtin')
            : tool.connection?.display_name.trim()
              ? tool.connection.display_name
              : t('bindings.picker.connectionUnknown'),
        source: tool.source,
        tools: [],
      }
      group.tools.push(tool)
      grouped.set(key, group)
    }
    return [...grouped.values()].sort((left, right) => {
      if (left.source !== right.source) {
        return left.source === 'builtin' ? -1 : 1
      }
      return left.label.localeCompare(right.label)
    })
  }, [t, tools])

  const filteredGroups = useMemo(() => {
    const keyword = deferredSearch.trim().toLocaleLowerCase()
    return groups
      .map((group) => {
        const groupMatches = group.label.toLocaleLowerCase().includes(keyword)
        const matchingTools = group.tools
          .filter((tool) => {
            if (!keyword || groupMatches) {
              return true
            }
            return [tool.display_name, tool.description, tool.remote_name ?? ''].some((value) =>
              value.toLocaleLowerCase().includes(keyword),
            )
          })
          .sort((left, right) => {
            const leftIdentity = agentToolIdentityKey(left)
            const rightIdentity = agentToolIdentityKey(right)
            const leftScore = Number(left.review_required) * 2 + Number(selected.has(leftIdentity))
            const rightScore =
              Number(right.review_required) * 2 + Number(selected.has(rightIdentity))
            return rightScore - leftScore || left.display_name.localeCompare(right.display_name)
          })
        return { ...group, tools: matchingTools }
      })
      .filter((group) => group.tools.length > 0)
  }, [deferredSearch, groups, selected])

  const visibleTools = useMemo(
    () => filteredGroups.flatMap((group) => group.tools),
    [filteredGroups],
  )
  const selectCurrentResults = () => {
    const next = new Set(selected)
    for (const tool of visibleTools) {
      if (tool.source === 'builtin' || tool.definition_id) {
        next.add(agentToolIdentityKey(tool))
      }
    }
    onSelectedChange(next)
  }

  return (
    <>
      <Drawer
        bodyClassName="min-h-0 flex-1 overflow-hidden"
        closeIcon={isLeftSecondary ? <CollapseIcon /> : undefined}
        closeLabel={isLeftSecondary ? t('bindings.actions.collapsePicker') : undefined}
        footer={
          <>
            <span className="mr-auto text-sm text-[var(--color-text-muted)]">
              {t('bindings.summary.selected', { count: selected.size })}
            </span>
            <Button onClick={onClose} variant="secondary">
              {t('actions.cancel')}
            </Button>
            <Button disabled={!hasDraftChanges} isLoading={isSaving} onClick={onSave}>
              {saveLabel ?? t('bindings.actions.save')}
            </Button>
          </>
        }
        headerAddon={
          <Input
            aria-label={t('bindings.picker.searchLabel')}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('bindings.picker.searchPlaceholder')}
            prefix={<SearchIcon />}
            value={search}
          />
        }
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
        modal={!isLeftSecondary}
        open
        panelClassName={
          isLeftSecondary
            ? 'lg:right-[640px] lg:w-[min(560px,calc(100vw-640px))] lg:max-w-none lg:border-r lg:border-[var(--color-divider)]'
            : undefined
        }
        size={isLeftSecondary ? 'md' : 'lg'}
        title={t('bindings.picker.title')}
        titleMeta={t('bindings.summary.selected', { count: selected.size })}
      >
        <div className="h-full min-h-0 overflow-y-auto p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--color-text-muted)]">
            <span>{t('bindings.picker.resultCount', { count: visibleTools.length })}</span>
            <div className="flex items-center gap-3">
              <button
                className="cursor-pointer font-medium text-[var(--color-primary)] hover:underline"
                onClick={selectCurrentResults}
                type="button"
              >
                {t('bindings.actions.selectCurrentResults')}
              </button>
              <button
                className="cursor-pointer font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:underline"
                disabled={isSaving}
                onClick={() => onSelectedChange(new Set())}
                type="button"
              >
                {t('bindings.actions.clear')}
              </button>
            </div>
          </div>

          {filteredGroups.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-[var(--color-panel-border)] px-4 text-center text-sm text-[var(--color-text-muted)]">
              {t('bindings.picker.empty')}
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredGroups.map((group, groupIndex) => {
                const expanded = Boolean(deferredSearch.trim()) || expandedGroups.has(group.key)
                const selectedCount = group.tools.filter((tool) =>
                  selected.has(agentToolIdentityKey(tool)),
                ).length
                const selectableTools = group.tools.filter(
                  (tool) => tool.source === 'builtin' || Boolean(tool.definition_id),
                )
                const allSelectableSelected =
                  selectableTools.length > 0 &&
                  selectableTools.every((tool) => selected.has(agentToolIdentityKey(tool)))

                const toggleGroupSelection = () => {
                  const next = new Set(selected)
                  for (const tool of group.tools) {
                    const identity = agentToolIdentityKey(tool)
                    if (allSelectableSelected) {
                      next.delete(identity)
                    } else if (tool.source === 'builtin' || tool.definition_id) {
                      next.add(identity)
                    }
                  }
                  onSelectedChange(next)
                }

                return (
                  <section
                    className="overflow-hidden rounded-lg border border-[var(--color-divider)] bg-white"
                    key={group.key}
                  >
                    <div className="flex min-h-12 items-stretch">
                      <button
                        aria-expanded={expanded}
                        aria-label={t('bindings.picker.toggleGroup', { name: group.label })}
                        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-3.5 text-left transition-colors hover:bg-[rgba(26,28,25,0.025)]"
                        onClick={() => {
                          if (deferredSearch.trim()) {
                            return
                          }
                          setExpandedGroups((current) => {
                            const next = new Set(current)
                            if (next.has(group.key)) {
                              next.delete(group.key)
                            } else {
                              next.add(group.key)
                            }
                            return next
                          })
                        }}
                        type="button"
                      >
                        <ToolSourceMark source={group.source} />
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--color-text)]">
                          {group.label}
                        </span>
                        <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                          {t('bindings.picker.groupSelection', {
                            selected: selectedCount,
                            total: group.tools.length,
                          })}
                        </span>
                        <ChevronIcon expanded={expanded} />
                      </button>
                      <button
                        aria-label={t(
                          allSelectableSelected
                            ? 'bindings.actions.clearGroupLabel'
                            : 'bindings.actions.selectGroupLabel',
                          { name: group.label },
                        )}
                        className="shrink-0 cursor-pointer border-l border-[var(--color-divider)] px-3 text-xs font-medium text-[var(--color-primary)] transition-colors hover:bg-[rgba(25,91,255,0.045)] disabled:cursor-not-allowed disabled:text-[var(--color-text-disabled)]"
                        disabled={isSaving || selectableTools.length === 0}
                        onClick={toggleGroupSelection}
                        type="button"
                      >
                        {t(
                          allSelectableSelected
                            ? 'bindings.actions.clearGroup'
                            : 'bindings.actions.selectGroup',
                        )}
                      </button>
                    </div>
                    {expanded ? (
                      <div className="divide-y divide-[var(--color-divider)] border-t border-[var(--color-divider)] bg-[rgba(26,28,25,0.012)] pl-10">
                        {group.tools.map((tool, toolIndex) => (
                          <ToolOptionRow
                            checkboxId={`${checkboxIdPrefix}-${String(groupIndex)}-${String(toolIndex)}`}
                            confirmedReviews={confirmedReviews}
                            key={agentToolIdentityKey(tool)}
                            onConfirmReview={onConfirmReview}
                            onSelectedChange={onSelectedChange}
                            placement={placement}
                            selected={selected}
                            tool={tool}
                          />
                        ))}
                      </div>
                    ) : null}
                  </section>
                )
              })}
            </div>
          )}
        </div>
      </Drawer>
    </>
  )
}
